/**
 * Claims Filing - shared constants and lightweight types.
 *
 * These are intentionally minimal and UI-agnostic so they can be reused
 * across components and hooks in the claims filing flow.
 */
import type { TemplateCategory } from '@/services/api/generated/templatesV2Service';

/**
 * Feature flags for Claims Filing. Centralized toggles for enabling/disabling
 * capabilities without scattering checks across components.
 */
export const CLAIMS_FILING_FEATURE_FLAGS = {
  /**
   * Controls whether Voice AI calls are enabled for THIRD_PARTY coverages.
   * Keep false until 3P support is live.
   */
  enableVoiceAIForThirdParty: true,
} as const;

/**
 * V2 TemplateCategory values that are considered LoR artifacts.
 * Used for filtering templates and document instances.
 */
export const LOR_TEMPLATE_CATEGORIES = new Set<TemplateCategory>([
  'LOR_GENERIC',
  'LOR_FIRST_PARTY',
  'LOR_THIRD_PARTY',
  'LOR_HEALTH',
  'PRESERVATION_LETTER',
]);

/**
 * V2 TemplateCategory values allowed for user selection when generating a new LoR.
 */
export const LOR_ALLOWED_TEMPLATE_CATEGORIES = new Set<TemplateCategory>([
  'LOR_GENERIC',
  'LOR_FIRST_PARTY',
  'LOR_THIRD_PARTY',
  'LOR_HEALTH',
  'PRESERVATION_LETTER',
]);

/**
 * Keys used to identify finalize panel fields and tie them to transcript
 * evidence highlighting.
 */
export type FinalFieldKey =
  | 'final:policy'
  | 'final:claim'
  | 'final:adjName'
  | 'final:adjEmail'
  | 'final:adjPhone'
  | 'final:adjFax';

/**
 * Canonical ordered list of finalize field keys.
 */
export const FINAL_FIELD_KEYS: FinalFieldKey[] = [
  'final:policy',
  'final:claim',
  'final:adjName',
  'final:adjEmail',
  'final:adjPhone',
  'final:adjFax',
];
